import {
  APP_INITIALIZER,
  ApplicationConfig,
  LOCALE_ID,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideRouter, withRouterConfig, RouteReuseStrategy, withPreloading } from '@angular/router';
import { provideNoopAnimations } from '@angular/platform-browser/animations';
import { provideIonicAngular } from '@ionic/angular/standalone';
import { ElectaRouteReuseStrategy } from '@core/routing/electa-route-reuse-strategy';
import { MobileTabPreloader } from '@core/routing/mobile-tab-preloader';

import { routes } from './app.routes';
import { provideStore } from '@ngrx/store';
import { provideEffects } from '@ngrx/effects';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { provideRouterStore, routerReducer } from '@ngrx/router-store';
import { auth9Reducer } from '@store/auth9user/auth9.reducer';
import { memberReducer } from '@store/member/member.reducer';
import { NZ_CONFIG } from 'ng-zorro-antd/core/config';
import { invitationReducer } from '@store/invitation/invitation.reducer';
import { provideServiceWorker } from '@angular/service-worker';
import { TranslateService } from '@core/services/translate.service';
import { environment } from '@env/environment';
import { SessionBootstrapService } from '@core/services/session-bootstrap.service';
import { ClientLoggerService } from '@core/logging/client-logger.service';
import { metaReducers } from '@store/app/reset-store.meta-reducer';
import { workReducer } from '@store/work/work.reducer';
import { WorkEffects } from '@store/work/work.effects';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalService } from 'ng-zorro-antd/modal';
import { CloseableMessageService } from '@core/services/closeable-message.service';

function initializeTranslations(translateService: TranslateService) {
  return () => translateService.initialize(environment.defaultLocale);
}

function initializeSession(bootstrap: SessionBootstrapService) {
  return () => bootstrap.init();
}

function initializeClientLogging(logger: ClientLoggerService) {
  return () => logger.initialize();
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(
      routes,
      withRouterConfig({ onSameUrlNavigation: 'reload' }),
      // Warm the lazy chunks of the five mobile tab routes after the initial
      // navigation so first tab taps don't flash blank (see MobileTabPreloader).
      withPreloading(MobileTabPreloader),
    ),
    { provide: RouteReuseStrategy, useClass: ElectaRouteReuseStrategy },
    provideIonicAngular(),
    ...(environment.disableAnimations ? [provideNoopAnimations()] : []),
    provideStore({
      auth9: auth9Reducer,
      member: memberReducer,
      invitation: invitationReducer,
      router: routerReducer,
      work: workReducer,
    }, { metaReducers }),
    provideRouterStore(),
    provideEffects(WorkEffects),
    ...(environment.production ? [] : [provideStoreDevtools({ maxAge: 25 })]),
    provideServiceWorker('ngsw-worker.js', {
      enabled: environment.serviceWorkerEnabled ?? environment.production,
      registrationStrategy: 'registerWhenStable:30000',
    }),
    NzModalService,
    { provide: NzMessageService, useClass: CloseableMessageService },
    {
      provide: NZ_CONFIG,
      useValue: {
        theme: {
          cssVariables: true,
        },
        select: {
          nzOptionHeightPx: 50,
        },
        message: {
          nzDuration: 5000,
          nzPauseOnHover: true,
          nzAnimate: true,
          nzTop: 'max(16px, calc(env(safe-area-inset-top, 0px) + 8px))',
        },
      },
    },
    {
      provide: LOCALE_ID,
      useValue: environment.defaultLocale ?? 'en',
    },
    {
      provide: APP_INITIALIZER,
      multi: true,
      useFactory: initializeTranslations,
      deps: [TranslateService],
    },
    {
      provide: APP_INITIALIZER,
      multi: true,
      useFactory: initializeSession,
      deps: [SessionBootstrapService],
    },
    {
      provide: APP_INITIALIZER,
      multi: true,
      useFactory: initializeClientLogging,
      deps: [ClientLoggerService],
    },
  ],
};
